import { useSupport } from '@/contexts/SupportContext';
import { track } from '@/lib/analytics';
import { AnalyticsEvents } from '@beacon/analytics-events';
import { Button } from '@beacon/ui/components';
import { useActiveFeatureFlags, usePostHog } from 'posthog-js/react';
import { matchPath, useLocation, useNavigate } from 'react-router';
import { z } from 'zod';

const SIDEBAR_PROMO_FLAG_PREFIX = 'sidebar-promo-';

const promoSchema = z.object({
  id: z.string(),
  match: z.string().min(1),
  priority: z.number().default(0),
  tag: z.string().optional(),
  heading: z.string().min(1),
  body: z.string().optional(),
  button: z
    .object({
      label: z.string().min(1),
      action: z.literal('support').optional(),
      href: z.string().optional(),
      to: z.string().optional(),
    })
    .optional(),
});

type Promo = z.infer<typeof promoSchema>;

// parse promo(s) from flag payload
function parsePromos(metadata: unknown): Promo[] {
  if (!metadata || typeof metadata !== 'object') return [];

  if ('payload' in metadata) {
    const { payload } = metadata;

    if (payload && typeof payload === 'object' && 'promos' in payload) {
      const result = z.array(promoSchema).safeParse(payload.promos);

      return result.success ? result.data : [];
    }

    const result = promoSchema.safeParse(metadata.payload);

    return result.success ? [result.data] : [];
  }

  return [];
}

export function SidebarAds({ collapsed }: { collapsed?: boolean }) {
  const { openSupport } = useSupport();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const posthog = usePostHog();
  const activeFlags = useActiveFeatureFlags();

  // promos only make sense in the expanded sidebar.
  if (collapsed) return null;

  const promo = (activeFlags ?? [])
    .filter((key) => key.startsWith(SIDEBAR_PROMO_FLAG_PREFIX))
    .flatMap((key) => parsePromos(posthog?.getFeatureFlagResult(key)))
    .filter((p) => matchPath(p.match, pathname))
    .sort((a, b) => b.priority - a.priority)[0];

  if (!promo) return null;

  const button = promo.button;
  const promoId = promo.id;

  const onButtonClick = () => {
    if (!button) return;

    track(AnalyticsEvents.SIDEBAR_PROMO_CLICKED, {
      promoId,
      match: promo.match,
      heading: promo.heading,
      buttonLabel: button.label,
      action: button.href ? 'href' : button.to ? 'route' : 'support',
      path: pathname,
    });

    if (button.href) {
      window.open(button.href, '_blank', 'noopener,noreferrer');
    } else if (button.to) {
      navigate(button.to);
    } else {
      openSupport('support', { source: 'sidebar_promo', promoId });
    }
  };

  return (
    <div className="border-stroke-1 border-t p-2">
      <div className="border-stroke-1 bg-surface-2/40 rounded border p-3">
        {promo.tag && (
          <div className="text-primary-9 font-mono text-[10.5px] font-medium tracking-[.12em] uppercase">
            <span className="bg-primary-9 mr-1.5 inline-block h-1.5 w-1.5 rounded-full align-middle" />
            {promo.tag}
          </div>
        )}

        <p className="text-text-1 mt-2 text-[13px] leading-snug font-medium">{promo.heading}</p>
        {promo.body && <p className="text-text-3 mt-1 text-[11px] leading-snug">{promo.body}</p>}

        {button && (
          <Button size="xs" mono onClick={onButtonClick} className="mt-3">
            {button.label}
          </Button>
        )}
      </div>
    </div>
  );
}
